import { EyeIcon } from '@heroicons/react/24/outline';
import { Tooltip } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { formatDate } from '../../../../common-utils/formatters';
import type { FixedNewsletter } from '../../../@types/Newsletter';
import { NEWSLETTER_TYPE_FIXED, NEWSLETTER_WORDING } from '../../../@types/Newsletter';
import type { RootState } from '../../../store/store';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import TemplateSelector from '../../common/TemplateSelector/TemplateSelector.module';

// Extended FixedNewsletter type to include templateCount for display
interface ExtendedFixedNewsletter extends FixedNewsletter {
  templateCount: number;
}

function NewsletterList() {
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [newsletters, setNewsletters] = useState<ExtendedFixedNewsletter[]>([]);
  const [fixedNewsletters, setFixedNewsletters] = useState<FixedNewsletter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedNewsletter, setSelectedNewsletter] = useState<FixedNewsletter | null>(null);
  const [showTemplatePopup, setShowTemplatePopup] = useState(false);

  const fetchNewsletters = async() => {
    if (!jwt) {return;}
    
    try {
      // Fetch fixed newsletters
      const fixedResponse = await fetch(
        `/api/fixed-newsletters`,
        JWTHeader(jwt),
      );
      
      if (!fixedResponse.ok) {
        throw new Error('Failed to fetch fixed newsletters');
      }
      
      const fixedData = await fixedResponse.json();
      
      // Store fixed newsletters as is for template selection
      setFixedNewsletters(fixedData);
      
      // Process fixed newsletters for the list - one entry per fixed newsletter
      const processedFixedNewsletters = fixedData.map((newsletter: FixedNewsletter) => ({
        ...newsletter,
        id: `fixed:${newsletter.date}:${newsletter.slug}`, // ID format without template
        templateCount: newsletter.templates.length,
      }));

      setNewsletters(processedFixedNewsletters);
    } catch (error) {
      console.error('Failed to fetch newsletters :', error);
      showToast('error', 'Une erreur est survenue lors du chargement des newsletters.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsletters();
  }, [jwt]);

  const handleTemplateSelection = (newsletter: FixedNewsletter, templateName: string) => {
    const newsletterId = `fixed:${newsletter.date}:${newsletter.slug}:${templateName}`;
    navigate(ROUTES.ADMIN_NEWSLETTER_PREVIEW.replace(':id', newsletterId));
    setShowTemplatePopup(false);
  };

  const handleViewNewsletter = (newsletter: ExtendedFixedNewsletter) => {
    // Find the corresponding fixed newsletter
    const fixedNewsletter = fixedNewsletters.find(fn => 
      `fixed:${fn.date}:${fn.slug}` === newsletter.id,
    );
    
    if (fixedNewsletter) {
      if (fixedNewsletter.templates.length === 1) {
        // If only one template, go directly to preview
        handleTemplateSelection(fixedNewsletter, fixedNewsletter.templates[0].name);
      } else {
        // Show template selection popup
        setSelectedNewsletter(fixedNewsletter);
        setShowTemplatePopup(true);
      }
    }
  };

  const headers = [
    { title: 'Titre' },
    { title: 'Type' },
    { title: 'Templates' },
    { title: 'Date de création' },
    { title: 'Actions' },
  ];

  const tableData = newsletters.map((newsletter) => ({
    'Titre': newsletter.title,
    'Type': NEWSLETTER_WORDING[NEWSLETTER_TYPE_FIXED],
    'Templates': `${newsletter.templateCount} template(s)`,
    'Date de création': newsletter.availableDate ? formatDate(newsletter.availableDate) : '-',
    'Actions': (
      <div className="flex space-x-2">
        <Tooltip content="Voir la newsletter">
          <button
            onClick={() => handleViewNewsletter(newsletter)}
            className="flex items-center space-x-1 rounded p-1 text-blue-600 hover:text-blue-800"
            disabled={!newsletter.templates || newsletter.templates.length === 0}
          >
            <EyeIcon className="h-5 w-5" />
            <span>Voir</span>
          </button>
        </Tooltip>
      </div>
    ),
  }));

  if (isLoading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Newsletters</h1>
            <Skeleton width={300} height={20} className="mt-2" />
          </div>
        </div>
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <Skeleton key={index} height={60} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Newsletters Fixes</h1>
            <p className="mt-2">
              Gérez vos newsletters fixes ici. Ces newsletters sont créées à partir de templates HTML prédéfinis.
            </p>
          </div>
        </div>

        <MUITable
          data={tableData}
          headers={headers}
          isSearchable
          sortableColumns={['Titre', 'Type', 'Date de création']}
        />
      </div>

      {/* Template Selection */}
      {selectedNewsletter && (
        <TemplateSelector
          isOpen={showTemplatePopup}
          onClose={() => setShowTemplatePopup(false)}
          newsletter={selectedNewsletter}
          onTemplateSelect={handleTemplateSelection}
        />
      )}
    </div>
  );
}

export default NewsletterList; 